// Matching bank statement money-out lines to contractor payments that were
// already recorded (Job Estimate Spreadsheet → Sub Pay, or Contractor Pay).
//
// A contractor payment is recorded once when you pay (amount, date, method,
// reference / note). The same money shows up again on the bank statement.
// Matching links the two so the payment is counted ONE time:
//   • Profit & Profit Sharing counts the bank line (as Subcontractors).
//   • The bank line does not create a second entry in the transactions ledger.
//   • The contractor payment is marked "verified on bank statement".

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return isNaN(n) ? 0 : n;
};
const norm = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
const dayNum = (d) => {
  const t = Date.parse(String(d || "").slice(0, 10) + "T12:00:00");
  return isNaN(t) ? null : Math.round(t / 86400000);
};

const toDateKey = (v) => {
  if (!v) return "";
  if (v?.toDate) return v.toDate().toISOString().slice(0, 10);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "object" && v.seconds) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return String(v).slice(0, 10);
};

/** One shape for payments from subcontractorPay and the older subcontractorPayments. */
export const normalizeContractorPayment = (id, data = {}, sourceCollection = "subcontractorPay") => ({
  id,
  sourceCollection,
  name: data.subcontractorName || data.userName || data.vendor || data.property || "Contractor",
  amount: num(data.amount ?? data.amountPaid),
  date: toDateKey(data.date || data.datePaid || data.createdAt),
  method: data.method || data.paymentMethod || "",
  reference: data.reference || "",
  note: data.note || "",
  jobLabel: data.jobLabel || "",
  bankEntryId: data.bankEntryId || "",
  bankVerified: !!data.bankVerified,
});

const METHOD_WORDS = {
  zelle: ["zelle"],
  check: ["check", "chk", "cheque"],
  ach: ["ach", "direct dep", "online transfer", "transfer"],
  "cash app": ["cash app", "cashapp", "square cash"],
  venmo: ["venmo"],
  paypal: ["paypal"],
  wire: ["wire"],
  cash: ["atm", "withdrawal", "cash"],
};

const methodKey = (method) => {
  const m = norm(method);
  return Object.keys(METHOD_WORDS).find((k) => m.includes(k)) || "";
};

// How many days apart the payment record and the bank line can be.
// Checks can take weeks to clear; electronic payments land within days.
const windowFor = (method) => (methodKey(method) === "check" ? 45 : 10);

/**
 * Score how well a contractor payment fits a bank line. Returns null when it
 * clearly doesn't fit. Higher is better; exact amount is required for an
 * automatic match.
 */
export const scorePaymentForLine = (line = {}, pay = {}) => {
  const lineAmt = num(line.amount);
  const diff = Math.abs(lineAmt - pay.amount);
  const exact = diff <= 0.009;

  const ld = dayNum(line.date);
  const pd = dayNum(pay.date);
  if (ld === null || pd === null) return null;
  const days = Math.abs(ld - pd);
  const window = windowFor(pay.method);
  if (days > window) return null;

  let score = 0;
  const reasons = [];
  if (exact) {
    score += 50;
    reasons.push("same amount");
  } else {
    return null; // only exact amounts are candidates for this line
  }
  score += Math.round(((window - days) / window) * 20);
  reasons.push(days === 0 ? "same day" : `${days} day${days === 1 ? "" : "s"} apart`);

  const desc = norm(`${line.description || ""} ${line.vendorOrPayer || ""} ${line.transactionDetail || ""}`);
  const nameParts = norm(pay.name).split(" ").filter((p) => p.length >= 3);
  if (nameParts.some((p) => desc.includes(p))) {
    score += 25;
    reasons.push("name on statement");
  }

  const mk = methodKey(pay.method);
  if (mk && METHOD_WORDS[mk].some((w) => desc.includes(w))) {
    score += 10;
    reasons.push(`${pay.method} on statement`);
  }
  if (mk === "check" && pay.reference && desc.includes(norm(pay.reference))) {
    score += 20;
    reasons.push("check # matches");
  }

  // "Logged the account in the note": reward a matching last 4, penalize a different one.
  // Only the note is checked for an account's last 4. The reference is often a
  // check number, which would look like an account number.
  const noteText = String(pay.note || "");
  const refDigits = String(pay.reference || "").replace(/\D/g, "");
  const last4s = (noteText.match(/\b\d{4}\b/g) || []).filter((d) => d !== refDigits);
  const lineLast4 = String(line.accountLast4 || "").replace(/\D/g, "").slice(-4);
  if (lineLast4 && last4s.length) {
    if (last4s.includes(lineLast4)) {
      score += 15;
      reasons.push("account in note");
    } else {
      score -= 30;
      reasons.push("note names a different account");
    }
  }
  const bank = norm(line.bankName);
  if (bank && norm(noteText).includes(bank)) score += 5;

  return { score, days, reasons };
};

/**
 * Best candidates for one bank line, strongest first.
 * `claimed` = payment ids already matched to other lines (skipped).
 */
export const contractorCandidates = (line, payments, claimed = new Set()) =>
  payments
    .filter((p) => !claimed.has(p.id) && (!p.bankEntryId || p.bankEntryId === line.id))
    .map((p) => ({ pay: p, fit: scorePaymentForLine(line, p) }))
    .filter((x) => x.fit)
    .sort((a, b) => b.fit.score - a.fit.score);

/** Match automatically only when one candidate clearly wins. */
export const autoPick = (candidates) => {
  if (!candidates.length) return null;
  if (candidates.length === 1) return candidates[0];
  const [a, b] = candidates;
  return a.fit.score - b.fit.score >= 15 ? a : null;
};

/**
 * Any unmatched contractor payment within 45 days of the line, for picking by
 * hand when the app can't tell (different amount, split payment, etc.).
 */
export const nearbyPayments = (line, payments, claimed = new Set()) => {
  const ld = dayNum(line.date);
  return payments
    .filter((p) => !claimed.has(p.id) && (!p.bankEntryId || p.bankEntryId === line.id))
    .map((p) => ({ pay: p, days: ld === null ? 999 : Math.abs((dayNum(p.date) ?? 99999) - ld) }))
    .filter((x) => x.days <= 45)
    .sort((a, b) => Math.abs(a.pay.amount - num(line.amount)) - Math.abs(b.pay.amount - num(line.amount)) || a.days - b.days);
};

/** Bank line that looks like payroll (ADP, Paychex, Gusto…). */
export const looksLikePayroll = (line = {}) => {
  const d = norm(`${line.description || ""} ${line.vendorOrPayer || ""}`);
  return line.category === "Payroll" || /\badp\b|paychex|gusto|payroll|wage pay|direct dep/.test(d);
};

/** Bank line that could be paying a contractor. */
export const looksLikeContractorPay = (line = {}) => {
  const d = norm(`${line.description || ""} ${line.vendorOrPayer || ""}`);
  return line.category === "Subcontractor" || /zelle|venmo|cash app|cashapp|check|chk|paypal|online transfer|wire/.test(d);
};
